import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Typography, useMediaQuery, useTheme } from "@mui/material";
import { PRODUCT_TOUR_VERSION } from "@chessla/shared";
import { Joyride, type Controls, type EventData, type Step } from "react-joyride";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchGames } from "@/features/games";
import { useAuth } from "@/app/context/AuthContext";
import { ALERT_RED, DARK_SURFACES } from "@/shared/theme";
import { markProductTourComplete } from "./product-tour.api";
import {
  PRODUCT_TOUR_EVENT,
  PRODUCT_TOUR_LAYER_IDS,
  fullProductTourRoutes,
  addCompletedProductTour,
  hasCompletedProductTour,
  haveAllProductToursCompleted,
  pendingProductTourLayer,
  productTourPageForPath,
  productTourPageForId,
  productTourProgressKey,
  resolveProductTourTarget,
  shouldPersistTourStatus,
  type ProductTourId,
  type ProductTourLayerId,
  type ProductTourPageId,
} from "./product-tour";
import { productTourFloatingOptions, productTourOptions, productTourStyles } from "./product-tour-style";
import { TutorialPractice } from "./TutorialPractice";
import { analyticsConsent, ANALYTICS_CONSENT_EVENT } from "@/shared/analytics/ga4";
import { BOARD_CONTROL_TOURS } from "./board-control-tours";
import { EVIDENCE_CONTROL_TOURS } from "./evidence-control-tours";
import { WORKSPACE_CONTROL_TOURS } from "./workspace-control-tours";
import { findControlTourTarget, prepareControlTourTarget, watchControlTourTarget } from "./control-tour-target";
import { ControlTourCopy } from "./ControlTourCopy";

type TourCallout = {
  body: string;
  eyebrow: string;
  placement?: Step["placement"];
  target?: string;
  title: string;
};

const PAGE_ROOTS: Record<ProductTourPageId, string> = {
  home: '[data-route-path="/home"]',
  archive: '[data-route-path="/archive"]',
  board: '[data-route-path="/board"]',
  settings: '[data-route-path="/settings"]',
  about: '[data-route-path="/about"]',
  games: '[data-route-path="/games"]',
  review: '[data-route-path^="/games/"]',
  patterns: '[data-route-path="/patterns"]',
  trends: '[data-route-path="/trends"]',
  chats: '[data-route-path="/chats"]',
  chat: '[data-route-path="/chat"]',
  analyze: '[data-route-path="/analyze"]',
  membership: '[data-route-path="/subscription"]',
  profile: '[data-route-path="/profile"]',
  help: '[data-route-path="/help"]',
};

const PAGE_TOURS: Record<ProductTourPageId, TourCallout[]> = {
  home: [
    {
      eyebrow: "HOME",
      title: "Your next step starts here.",
      body: "Resume a review, inspect recent results, or analyze your first game. Choose a format to keep dashboard evidence relevant.",
    },
    {
      eyebrow: "YOUR DATA",
      title: "Small samples need context.",
      body: "Imported results are not analyzed evidence. With few analyzed games, treat patterns as early observations; incomplete comparisons are not shown as progress.",
      target: '[data-testid="home-dashboard"]',
    },
  ],
  archive: [
    {
      eyebrow: "ARCHIVE",
      title: "Manage your game library.",
      body: "Find imported and analyzed games, open a saved review, or select games for analysis.",
    },
    {
      eyebrow: "FILTERS",
      title: "Find the games you need.",
      body: "Narrow the library by format, clock, and analysis status before selecting games.",
      target: '[aria-label="Archive filters"]',
    },
    {
      eyebrow: "GAME MANAGEMENT",
      title: "Review, analyze, or remove deliberately.",
      body: "Selection lets you prepare a batch for analysis. Deleting games is a separate action that requires confirmation.",
      target: '[aria-label="Game archive"]',
    },
  ],
  board: [
    {
      eyebrow: "BOARD",
      title: "Explore a position.",
      body: "Use this workspace to examine moves and alternatives. It is separate from the recorded history of your imported games.",
    },
    {
      eyebrow: "BOARD EVIDENCE",
      title: "Keep your eyes on the current position.",
      body: "Inspect the available evidence, change board settings, and navigate the line. Verified findings and temporary exploration remain distinct.",
      target: '[data-testid="board-position"]',
    },
  ],
  settings: [
    {
      eyebrow: "SETTINGS",
      title: "Make Chessla work your way.",
      body: "Configure appearance, boards, game defaults, analysis, Nikola privacy, and notifications here.",
    },
    {
      eyebrow: "SECTIONS",
      title: "Choose the settings you want to change.",
      body: "Each section groups related preferences. Coaching context is information you provide, not a verified finding about your chess.",
      target: '[aria-label="Settings sections"]',
    },
  ],
  about: [
    {
      eyebrow: "ABOUT CHESSLA",
      title: "Understand the product.",
      body: "Learn how your games, verified board evidence, Patterns, and Nikola fit together. Visit Help for detailed instructions or replay a page tour whenever you need it.",
    },
  ],
  games: [
    {
      eyebrow: "GAMES / YOUR LIBRARY",
      title: "Start with the right slice.",
      body: "Choose a format and clock before reading results. Rapid, Blitz, Bullet, and Daily stay separate so the comparison remains useful.",
    },
    {
      eyebrow: "SCOPE",
      title: "Filter before you judge.",
      body: "Each format card shows the games available to study. Choose one to reveal its clock groups, then open the combined library or a specific clock.",
      target: '[data-tour="games-format-picker"]',
    },
    {
      eyebrow: "OPEN A GAME",
      title: "Every row leads to evidence.",
      body: "Open an analyzed game for Review. Games without analysis remain available and can be sent to Analyze when you are ready.",
      target: '[data-testid="games-table-body"]',
    },
  ],
  review: [
    {
      eyebrow: "GAME REVIEW",
      title: "The board is the source of truth.",
      body: "Move through the game one decision at a time. Board markers, evaluations, clocks, and continuations stay tied to the exact position.",
    },
    {
      eyebrow: "REVIEW MODES",
      title: "Choose how much help you want.",
      body: "Guided Review moves through Think, Test, Verify, and Carry. Open lets you inspect the saved evidence freely. Choose Practice on this page to work through a real decision with a checklist beside the controls.",
      target: '[data-tour="review-tabs"]',
    },
    {
      eyebrow: "BOARD CONTROLS",
      title: "One position across every tab.",
      body: "Move navigation, orientation, evidence marks, and temporary lines stay synchronized while the right workspace changes.",
      target: '[data-testid="game-review-board-rail"]',
    },
  ],
  patterns: [
    {
      eyebrow: "PATTERNS",
      title: "Look for recurrence, not one drama.",
      body: "Patterns group repeated decision evidence from analyzed games inside one format and time window.",
    },
    {
      eyebrow: "BROWSE BY SKILL",
      title: "Choose an area without losing your filters.",
      body: "Skill cards narrow the patterns shown below. The evidence card names the selected skill; Show all patterns clears it without changing your format or clock.",
      target: '[aria-label="Development overview"]',
    },
    {
      eyebrow: "PATTERN CARD",
      title: "Switch patterns in place.",
      body: "Choose an icon card to inspect a pattern. The highlighted card is the one you are viewing; counts show recorded evidence, not mastery. Previous and next evidence arrows browse its available moments.",
      target: '[data-testid="pattern-detail-card"]',
    },
    {
      eyebrow: "EXAMPLES",
      title: "Open the positions behind the pattern.",
      body: "Each example returns to a real game and exact move. A single occurrence is evidence, not a recurring habit.",
      target: '[data-testid="pattern-evidence"]',
    },
  ],
  trends: [
    {
      eyebrow: "TRENDS",
      title: "Read direction, then inspect the points.",
      body: "Select a format and clock to keep comparisons honest. Rating, accuracy, errors, activity, and repertoire remain separate signals.",
    },
    {
      eyebrow: "CHARTS",
      title: "Hover or touch for exact values.",
      body: "Charts summarize your analyzed game history. When available, forecasts show a nominal model range (not a guarantee) and measured held-out coverage.",
      target: "figure",
    },
  ],
  chats: [
    {
      eyebrow: "NIKOLA / CONVERSATIONS",
      title: "Resume context or start clean.",
      body: "Open an earlier conversation when its context still matters. Start a new one when you want a clean question and tool trail.",
    },
    {
      eyebrow: "NEW CHAT",
      title: "Ask naturally.",
      body: "Nikola decides when your games are relevant and when a direct chess answer is enough. You do not need command syntax.",
      target: '[data-tour="chats-new"]',
    },
  ],
  chat: [
    {
      eyebrow: "NIKOLA / CHAT",
      title: "Ask the question you actually have.",
      body: "Use ordinary language. Nikola can inspect owned games, exact positions, profile facts, and trends when the answer needs them.",
    },
    {
      eyebrow: "CONVERSATION",
      title: "Answers and checks stay together.",
      body: "Follow Nikola's response, compact work summary, and any verified game references in one chronological thread.",
      target: '[data-tour="chat-thread"]',
    },
    {
      eyebrow: "YOUR MESSAGE",
      title: "Add context only when it helps.",
      body: "Mention a format, date range, game, or move when you care about that scope. Verified game references remain attached to the answer.",
      target: '[data-tour="chat-composer"]',
    },
  ],
  analyze: [
    {
      eyebrow: "ANALYZE",
      title: "Turn a game into verified facts.",
      body: "Import finished games, choose an analysis depth, and keep this page open until saving finishes.",
    },
    {
      eyebrow: "ANALYSIS DEPTH",
      title: "Choose how far to search.",
      body: "Quick, Regular, and Deep change calculation depth and time. They never change the legality of the recorded game.",
      target: '[data-tour="analysis-presets"]',
    },
    {
      eyebrow: "START ANALYSIS",
      title: "Depth changes work, not truth.",
      body: "Quick, Regular, and Deep control how much calculation runs. Saved evidence then powers Review, Patterns, Trends, and Nikola.",
      target: '[data-tour="analysis-queue"]',
    },
    {
      eyebrow: "ANALYSIS HISTORY",
      title: "Completed work stays reusable.",
      body: "Open a finished game in Review or reanalyze it when newer evidence is available.",
      target: '[data-tour="analysis-history"]',
    },
  ],
  membership: [
    {
      eyebrow: "MEMBERSHIP",
      title: "See access and credits together.",
      body: "Your active plan, included credits, purchased credits, and billing actions live here.",
    },
    {
      eyebrow: "PLANS AND CREDITS",
      title: "Choose only when you need more.",
      body: "Compare the available options, then use the matching action to manage the plan or add credits.",
      target: '[data-tour="membership-overview"]',
    },
    {
      eyebrow: "CURRENT STATUS",
      title: "Read access before changing it.",
      body: "The status panel shows active access and balance context before any billing action begins.",
      target: '[data-tour="membership-status"]',
    },
  ],
  profile: [
    {
      eyebrow: "PROFILE",
      title: "Your account, chess identity, and privacy.",
      body: "Update personal details, password, and public chess username from this dashboard. Open Settings for coaching context, email preferences, and Nikola data controls.",
    },
    {
      eyebrow: "NIKOLA CONTEXT",
      title: "Tell your coach what matters.",
      body: "Goals, self-reported strengths, and weaknesses guide tone and relevance. They remain your notes, not verified chess facts.",
      target: '[data-tour="profile-settings"]',
    },
    {
      eyebrow: "MEMBERSHIP",
      title: "Manage access in one place.",
      body: "Open Membership to review plan and credit controls.",
      target: '[data-tour="profile-membership"]',
    },
    {
      eyebrow: "ACCOUNT PRIVACY",
      title: "Sensitive actions stay explicit.",
      body: "Account deletion requires deliberate confirmation.",
      target: '[data-tour="profile-privacy"]',
    },
    {
      eyebrow: "TOURS",
      title: "Replay help whenever you need it.",
      body: "Replay starts the complete platform tutorial at Home. Use its section controls to follow the full learning loop.",
      target: '[data-tour="profile-tour-replay"]',
    },
  ],
  help: [
    {
      eyebrow: "OPERATING MANUAL",
      title: "Learn the evidence language.",
      body: "Help explains Game Review, Principles, Patterns, Trends, Nikola, privacy, and the visual markers used across Chessla.",
    },
    {
      eyebrow: "HELP SECTIONS",
      title: "Jump to the exact subject.",
      body: "Navigate directly to board markers, Review, Patterns, Trends, Nikola, account controls, or privacy.",
      target: '[data-tour="help-sections"]',
    },
    {
      eyebrow: "FIELD GUIDE",
      title: "Read the meaning beside the example.",
      body: "Each section explains what a control proves, what it does not prove, and where to inspect the underlying evidence.",
      target: '[data-tour="help-article"]',
    },
    {
      eyebrow: "JUMP TO A TOPIC",
      title: "Use the manual as a reference.",
      body: "Choose a topic to move directly to its explanation, then return whenever a label or board marker is unclear.",
      target: "button",
    },
  ],
};

const LAYER_TOURS: Record<ProductTourLayerId, TourCallout[]> = {
  "games:format": [
    {
      eyebrow: "GAMES / FORMAT",
      title: "Pick the kind of chess.",
      body: "Rapid, Blitz, Bullet, and Daily stay separate. Every card shows how many imported games are available.",
      target: '[data-tour="games-format"]',
    },
    {
      eyebrow: "FORMAT CARDS",
      title: "Counts come before conclusions.",
      body: "Choose one format to reveal its clocks. All remains last and combines formats only where an aggregate is useful.",
      target: '[data-tour="games-format-picker"]',
    },
  ],
  "games:clock": [
    {
      eyebrow: "GAMES / CLOCK",
      title: "Now choose the pace.",
      body: "Exact clocks keep ten-minute games apart from increment and other controls inside the same format.",
      target: '[data-tour="games-clock"]',
    },
    {
      eyebrow: "CLOCK CARDS",
      title: "Combine clocks deliberately.",
      body: "All clocks sums every clock inside the selected format. Individual cards keep the narrower comparison.",
      target: '[data-tour="games-clock-picker"]',
    },
  ],
  "games:results": [
    {
      eyebrow: "GAMES / RESULTS",
      title: "This is the selected library.",
      body: "The heading records format and clock. Use the numbered rail to change either scope without losing your library.",
      target: '[data-tour="games-results"]',
    },
    {
      eyebrow: "GAME ROWS",
      title: "Every row leads somewhere useful.",
      body: "Read result, opponent, opening, rating, date, and analysis state. Open verified Review or send unfinished work to Analyze.",
      target: '[data-testid="games-table-body"]',
    },
  ],
  "patterns:format": [
    {
      eyebrow: "PATTERNS / FORMAT",
      title: "Start with one playing environment.",
      body: "A recurrence means more when its games share a format. Counts show the sample before interpretation.",
      target: '[data-tour="patterns-format"]',
    },
    {
      eyebrow: "FORMAT CARDS",
      title: "Choose the largest useful sample.",
      body: "Formats are ordered by game count. Select one to reveal the exact clocks inside it.",
      target: '[data-tour="patterns-format-picker"]',
    },
  ],
  "patterns:clock": [
    {
      eyebrow: "PATTERNS / CLOCK",
      title: "Control for pace.",
      body: "Decision habits change with time pressure. Choose one exact clock before comparing recurrences.",
      target: '[data-tour="patterns-clock"]',
    },
    {
      eyebrow: "CLOCK CARDS",
      title: "Keep the cohort coherent.",
      body: "Every card shows its available games. Patterns avoids All here because a blended pace weakens the analysis.",
      target: '[data-tour="patterns-clock-picker"]',
    },
  ],
  "patterns:results": [
    {
      eyebrow: "PATTERNS / SUMMARY",
      title: "Read recurrence before interpretation.",
      body: "Each tile is one measured habit from this format. Counts come first; explanation follows the facts.",
      target: '[data-tour="summary-strip"]',
    },
    {
      eyebrow: "PATTERNS / LIST",
      title: "Pick any pattern; the board stays put.",
      body: "Tactical patterns and principles share one list. The row you are viewing is marked. On a phone, a tap opens the board full screen.",
      target: '[data-tour="explorer-list"]',
    },
    {
      eyebrow: "PATTERN EVIDENCE",
      title: "Open the positions behind it.",
      body: "Step through each saved position from your own games. One occurrence is evidence, not a recurring habit.",
      target: '[data-tour="explorer-panel"]',
    },
  ],
  "trends:format": [
    {
      eyebrow: "TRENDS / FORMAT",
      title: "Choose one rating pool.",
      body: "Every format has its own rating and decision environment. Select one before reading direction.",
      target: '[data-tour="trends-format"]',
    },
    {
      eyebrow: "FORMAT CARDS",
      title: "The count is your sample.",
      body: "Cards show available games by format. Choose one to reveal its exact clock groups.",
      target: '[data-tour="trends-format-picker"]',
    },
  ],
  "trends:clock": [
    {
      eyebrow: "TRENDS / CLOCK",
      title: "Compare like with like.",
      body: "Choose the exact pace whose rating, accuracy, errors, activity, and repertoire you want to follow.",
      target: '[data-tour="trends-clock"]',
    },
    {
      eyebrow: "CLOCK CARDS",
      title: "Avoid a blended signal.",
      body: "Trends avoids All clocks because pace changes what the chart means.",
      target: '[data-tour="trends-clock-picker"]',
    },
  ],
  "trends:results": [
    {
      eyebrow: "TRENDS / RESULTS",
      title: "Read each signal separately.",
      body: "Rating, accuracy, errors, activity, and repertoire answer different questions. Direction is descriptive, not destiny.",
      target: '[data-tour="trends-results"]',
    },
    {
      eyebrow: "CHART DETAIL",
      title: "Inspect the points.",
      body: "Hover or touch for exact values. Forecasts appear only when enough calibrated evidence exists.",
      target: "figure",
    },
  ],
  "review:review": [
    {
      eyebrow: "REVIEW / DECISION",
      title: "Reconstruct the choice first.",
      body: "Guided mode can hide later evidence while you think. Open mode lets you browse saved evidence freely.",
      target: '[data-tour="review-review-panel"]',
    },
    {
      eyebrow: "REVIEW MODE",
      title: "Choose guided or open.",
      body: "Guided review moves through Think, Test, Verify, and Carry. Predict a reply, test your idea, inspect the consequence, and save one next-game cue. Open review exposes saved evidence immediately.",
      target: '[aria-label="Review mode"]',
    },
    {
      eyebrow: "EXACT POSITION",
      title: "Test a legal alternative.",
      body: "Temporary board moves never alter the recorded game. Restore the position or select another move at any time.",
      target: '[data-testid="game-review-board-rail"]',
    },
  ],
  "review:nikola": [
    {
      eyebrow: "REVIEW / NIKOLA",
      title: "Ask about this game naturally.",
      body: "Nikola receives selected game and move context. Ask why, compare plans, or request a simpler explanation.",
      target: '[data-tour="review-nikola-panel"]',
    },
    {
      eyebrow: "THREAD",
      title: "Position context follows the conversation.",
      body: "Verified references can move the board to a cited moment. The recorded game remains unchanged.",
      target: '[data-testid="game-nikola-thread"]',
    },
    {
      eyebrow: "MESSAGE",
      title: "Write like a person.",
      body: "Ask directly. Add a move or position only when you want to narrow the current context.",
      target: '[data-testid="chat-composer"]',
    },
  ],
  "review:moves": [
    {
      eyebrow: "REVIEW / MOVES",
      title: "Read the game move by move.",
      body: "Select any move to synchronize board, evaluation, clocks, classification, and saved evidence.",
      target: '[data-tour="review-moves-panel"]',
    },
    {
      eyebrow: "MOVE EVIDENCE",
      title: "Played move and better line stay distinct.",
      body: "Badges, arrows, and continuations identify the visible layer. Select a line to preview it safely.",
      target: '[data-testid="move-evidence-strip"]',
    },
    {
      eyebrow: "TIMELINE",
      title: "Jump to the turning point.",
      body: "Evaluation and think time share one timeline. Select a point to open that exact ply.",
      target: 'svg[aria-label^="Evaluation from"]',
    },
  ],
  "review:details": [
    {
      eyebrow: "REVIEW / DETAILS",
      title: "Start with the game record.",
      body: "Players, result, opening, ratings, date, clock, and source link describe the recorded game.",
      target: '[data-tour="review-details-panel"]',
    },
    {
      eyebrow: "EVIDENCE MATRIX",
      title: "Nothing is silently omitted.",
      body: "Move quality, every tactical motif, and every applied or missed principle remain visible even when a count is zero.",
      target: '[aria-label="Game evidence summary"]',
    },
    {
      eyebrow: "SELECT A BADGE",
      title: "Open the exact occurrence.",
      body: "A nonzero count expands its moves. Select one to jump to that ply and preview its verified continuation when available.",
      target: '[aria-label="Game evidence summary"]',
    },
  ],
};

const TOURS: Record<ProductTourId, TourCallout[]> = { ...PAGE_TOURS, ...LAYER_TOURS };

const TourCopy = ({
  body,
  eyebrow,
  title,
  onPractice,
}: Pick<TourCallout, "body" | "eyebrow" | "title"> & { onPractice: () => void }) => (
  <Box sx={{ textAlign: "left" }}>
    <Typography sx={{ color: ALERT_RED.dark, fontSize: ".64rem", fontWeight: 950, letterSpacing: ".14em", mb: 1.25 }}>
      {eyebrow}
    </Typography>
    <Typography
      component="h2"
      sx={{
        color: DARK_SURFACES.canvas,
        fontSize: "1.28rem",
        fontWeight: 950,
        letterSpacing: "-.035em",
        lineHeight: 1.08,
        mb: 1.25,
      }}
    >
      {title}
    </Typography>
    <Typography sx={{ color: DARK_SURFACES.canvas, fontSize: ".82rem", lineHeight: 1.6 }}>{body}</Typography>
    <Button onClick={onPractice} sx={{ mt: 1 }}>
      Practice on this page
    </Button>
  </Box>
);

const buildSteps = (tour: ProductTourId, compact: boolean, onPractice: () => void, concise = false): Step[] => {
  if (typeof document === "undefined") return [];
  const page = productTourPageForId(tour);
  const root = PAGE_ROOTS[page];
  const sidePlacement: Step["placement"] = compact ? "bottom" : "right";
  const controlTours = { ...BOARD_CONTROL_TOURS, ...EVIDENCE_CONTROL_TOURS, ...WORKSPACE_CONTROL_TOURS };
  const pageControls = tour === page ? controlTours[page] : undefined;
  if (pageControls) {
    return pageControls.filter((step) => findControlTourTarget(root, step) ||
      (step.reveal && findControlTourTarget(root, { target: step.reveal }))).map((step) => ({
      target: () => findControlTourTarget(root, step),
      before: () => prepareControlTourTarget(root, step),
      content: <ControlTourCopy title={step.title} body={step.body} />,
      placement: compact || (findControlTourTarget(root, step)?.getBoundingClientRect().width ?? 0) > 480 ? "bottom" : "right",
      skipBeacon: true,
      skipScroll: true,
    }));
  }
  const controls = TOURS[tour].filter((callout) => callout.target && document.querySelector(`${root} ${callout.target}`));
  return (concise && controls.length ? controls : TOURS[tour]).flatMap((callout, index) => {
    const target = resolveProductTourTarget(root, callout.target, (selector) =>
      Boolean(document.querySelector(selector)),
    );
    if (!target) return [];
    return [
      {
        target,
        content: concise ? <ControlTourCopy title={callout.title} body={callout.body} /> : (
          <TourCopy body={callout.body} eyebrow={callout.eyebrow} title={callout.title} onPractice={onPractice} />
        ),
        placement: callout.placement ?? (concise && callout.target ? sidePlacement : index === 0 ? "center" : sidePlacement),
        skipBeacon: true,
        skipScroll: true,
        before: () => prepareControlTourTarget("body", { target }),
      },
    ];
  });
};

const readProgress = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeProgress = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // A blocked storage API may repeat a tour, but must not block the product.
  }
};

export const ProductTour = ({ onActiveChange }: { onActiveChange?: (active: boolean) => void }) => {
  const { account, onboardingCompleted, refetch, userId } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down("md"));
  const page = productTourPageForPath(pathname);
  const progressKey = userId ? productTourProgressKey(userId) : null;
  const [run, setRun] = useState(false);
  const stopTargetWatch = useRef<(() => void) | null>(null);
  useEffect(() => () => stopTargetWatch.current?.(), []);
  const [consentPending, setConsentPending] = useState(() => !analyticsConsent());
  useEffect(() => {
    onActiveChange?.(run && !consentPending);
    return () => {
      stopTargetWatch.current?.();
      onActiveChange?.(false);
    };
  }, [run, consentPending, onActiveChange]);
  useEffect(() => {
    const update = () => setConsentPending(!analyticsConsent());
    window.addEventListener(ANALYTICS_CONSENT_EVENT, update);
    return () => window.removeEventListener(ANALYTICS_CONSENT_EVENT, update);
  }, []);
  const [practice, setPractice] = useState(false);
  const [steps, setSteps] = useState<Step[]>([]);
  const [tourRevision, setTourRevision] = useState(0);
  const [fullTour, setFullTour] = useState<ReturnType<typeof fullProductTourRoutes> | null>(null);
  const replayRequest = useRef(0);
  const activeTour = useRef<ProductTourId | null>(null);
  const practiceActive = useRef(false);
  const handled = useRef(false);
  const layerStartTimer = useRef<number | null>(null);
  const syncing = useRef(false);

  const startTour = useCallback(
    (tour: ProductTourId, force = false, controls = true) => {
      if (!onboardingCompleted || !page || productTourPageForId(tour) !== page) return;
      if (!force && tour.includes(":")) return;
      if (!force && practiceActive.current) return;
      if (!force) {
        if (!account || !progressKey || account.productTourVersion >= PRODUCT_TOUR_VERSION) return;
        if (hasCompletedProductTour(readProgress(progressKey), tour)) return;
      }
      if (!controls) {
        practiceActive.current = true;
        activeTour.current = tour;
        setRun(false);
        setPractice(true);
        return;
      }
      practiceActive.current = false;
      setPractice(false);
      const nextSteps = buildSteps(tour, compact, () => {
        practiceActive.current = true;
        setRun(false);
        setPractice(true);
      }, true);
      if (nextSteps.length === 0) return;
      activeTour.current = tour;
      handled.current = false;
      setSteps(nextSteps);
      setTourRevision((revision) => revision + 1);
      setRun(true);
    },
    [account, compact, fullTour, onboardingCompleted, page, progressKey],
  );

  const startCurrentTour = useCallback(
    (controls = false) => {
      if (page) startTour(page, true, controls);
    },
    [page, startTour],
  );

  useEffect(() => () => window.clearTimeout(layerStartTimer.current ?? undefined), []);

  useEffect(() => {
    setFullTour(null);
    setPractice(false);
    practiceActive.current = false;
    return () => { replayRequest.current += 1; };
  }, [userId]);

  useEffect(() => {
    const start = (event: Event) => {
      const detail: unknown = event instanceof CustomEvent ? event.detail : undefined;
      if (detail && typeof detail === "object" && "mode" in detail && detail.mode === "full") {
        if (!userId || !onboardingCompleted) return;
        const request = ++replayRequest.current;
        setFullTour(fullProductTourRoutes(null));
        practiceActive.current = false;
        setRun(false);
        setPractice(false);
        navigate("/home");
        void fetchGames(1, 0, { analyzed: true }).then((games) => {
          if (replayRequest.current === request) setFullTour(fullProductTourRoutes(games[0]?.id ?? null));
        }).catch(() => undefined);
        return;
      }
      startCurrentTour(true);
    };
    window.addEventListener(PRODUCT_TOUR_EVENT, start);
    return () => window.removeEventListener(PRODUCT_TOUR_EVENT, start);
  }, [navigate, onboardingCompleted, startCurrentTour, userId]);

  useEffect(() => {
    if (!fullTour || !page) return;
    const timer = window.setTimeout(() => startTour(page, true, true), 700);
    return () => window.clearTimeout(timer);
  }, [fullTour, page, startTour]);

  useEffect(() => {
    window.clearTimeout(layerStartTimer.current ?? undefined);
    layerStartTimer.current = null;
    setRun(false);
    activeTour.current = null;
    handled.current = false;
    if (!onboardingCompleted || !account || !page || !progressKey) return;
    if (fullTour) return;
    if (account.productTourVersion >= PRODUCT_TOUR_VERSION) return;

    const progress = readProgress(progressKey);
    if (haveAllProductToursCompleted(progress)) {
      if (!syncing.current) {
        syncing.current = true;
        void markProductTourComplete()
          .then(() => refetch())
          .catch(() => undefined)
          .finally(() => {
            syncing.current = false;
          });
      }
      return;
    }
    if (hasCompletedProductTour(progress, page)) {
      const timer = window.setTimeout(() => {
        const root = document.querySelector(PAGE_ROOTS[page]);
        const marker = root?.matches("[data-tour-layer]") ? root : root?.querySelector("[data-tour-layer]");
        const layer = pendingProductTourLayer(marker?.getAttribute("data-tour-layer") ?? null, page, progress);
        if (layer) startTour(layer);
      }, 700);
      return () => window.clearTimeout(timer);
    }

    const timer = window.setTimeout(() => startTour(page), 700);
    return () => window.clearTimeout(timer);
  }, [account, fullTour, onboardingCompleted, page, progressKey, refetch, startTour]);

  useEffect(() => {
    if (!onboardingCompleted || !account || !page || !progressKey) return;
    if (account.productTourVersion >= PRODUCT_TOUR_VERSION || typeof MutationObserver === "undefined") return;

    let timer: number | undefined;
    const observer = new MutationObserver((records) => {
      if (activeTour.current || !hasCompletedProductTour(readProgress(progressKey), page)) return;
      for (const record of records) {
        if (!(record.target instanceof Element) || !record.target.closest(PAGE_ROOTS[page])) continue;
        const layer = pendingProductTourLayer(
          record.target.getAttribute("data-tour-layer"),
          page,
          readProgress(progressKey),
        );
        if (!layer) continue;
        window.clearTimeout(timer);
        timer = window.setTimeout(() => startTour(layer), 180);
        break;
      }
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["data-tour-layer"],
      subtree: true,
    });
    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }, [account, onboardingCompleted, page, progressKey, startTour]);

  const onEvent = (data: EventData, controls: Controls) => {
    if (data.type === "tooltip") {
      stopTargetWatch.current?.();
      const raw = data.step.target;
      const target = typeof raw === "function" ? raw() : typeof raw === "string" ? document.querySelector(raw) : raw;
      if (target instanceof HTMLElement) {
        stopTargetWatch.current = watchControlTourTarget(target, () => controls.replay());
      }
    } else if (data.type === "step:before_hook" || data.type === "step:after" || data.type === "tour:end") {
      stopTargetWatch.current?.();
      stopTargetWatch.current = null;
    }
    const tour = activeTour.current;
    if (!shouldPersistTourStatus(data.status) || handled.current || !tour || !progressKey) return;
    handled.current = true;
    activeTour.current = null;
    setRun(false);
    const progress = addCompletedProductTour(readProgress(progressKey), tour);
    writeProgress(progressKey, progress);
    if (fullTour) {
      const next = data.status === "finished" ? fullTour[fullTour.findIndex((entry) => entry.page === page) + 1] : null;
      if (next) navigate(next.path);
      else { replayRequest.current += 1; setFullTour(null); }
      return;
    }
    if (data.status === "finished" && tour === page) {
      const root = document.querySelector(PAGE_ROOTS[page]);
      const marker = root?.matches("[data-tour-layer]") ? root : root?.querySelector("[data-tour-layer]");
      const layer = pendingProductTourLayer(marker?.getAttribute("data-tour-layer") ?? null, page, progress);
      if (layer) {
        layerStartTimer.current = window.setTimeout(() => {
          layerStartTimer.current = null;
          startTour(layer);
        }, 320);
      }
    }
    if (!haveAllProductToursCompleted(progress) || syncing.current) return;
    syncing.current = true;
    void markProductTourComplete()
      .then(() => refetch())
      .catch(() => undefined)
      .finally(() => {
        syncing.current = false;
      });
  };

  const options = useMemo(() => productTourOptions(compact), [compact]);
  const styles = useMemo(
    () => productTourStyles(compact, theme.typography.fontFamily ?? "monospace"),
    [compact, theme],
  );

  if (!onboardingCompleted || !page) return null;
  if (practice)
    return (
      <TutorialPractice
        key={userId}
        page={page}
        userId={userId ?? "guest"}
        onClose={() => {
          replayRequest.current += 1;
          setFullTour(null);
          // Closing dismisses automatic help; saved practice checkpoints remain replayable.
          if (progressKey) {
            const dismissed = PRODUCT_TOUR_LAYER_IDS.filter((layer) => productTourPageForId(layer) === page).reduce(
              (progress, layer) => addCompletedProductTour(progress, layer),
              addCompletedProductTour(readProgress(progressKey), page),
            );
            writeProgress(progressKey, dismissed);
          }
          practiceActive.current = false;
          setPractice(false);
          setRun(false);
          activeTour.current = null;
        }}
      />
    );
  return (
    <Joyride
      key={tourRevision}
      continuous
      onEvent={onEvent}
      run={run && !consentPending}
      scrollToFirstStep
      steps={steps}
      locale={{
        back: "Back",
        last: fullTour && fullTour.at(-1)?.page !== page ? "Next page" : "Finish",
        next: "Next",
        nextWithProgress: "Next · {current}/{total}",
        skip: "Skip this guide",
      }}
      options={options}
      floatingOptions={productTourFloatingOptions}
      styles={styles}
    />
  );
};
